import { lazy, Suspense } from 'react';

import type { LeadFilters } from '../api/endpoints';
import { Hub, type HubTab } from '../components/Hub';
import { PageLoader } from '../components/Loader';
import { useFieldOnly, useProject } from '../projects/context';
import { BuyerRequestsPage } from './BuyerRequestsPage';
import { BrokerContacts } from './leads/BrokerContacts';
import { useBrokerLeads } from './leads/useBrokerLeads';
import { LeadsPage } from './LeadsPage';
import { RegionsPage } from './RegionsPage';
import { LeadCreateButton } from './leads/LeadCreateButton';

// Karte (Leaflet) erst laden, wenn der Reiter geöffnet wird.
const MapPage = lazy(() => import('./MapPage').then((m) => ({ default: m.MapPage })));

const withMyAppointments: LeadFilters = { appointment_user_id: 'me' };

// Makler (OFFENE_FRAGEN B-28): keine Karte des ganzen Projekts, keine Regionen (Fokus). Reiter „Mit Termin“ (Leads, bei
// denen er Termine hatte oder hat), „Kontakte & Nachfassen“ (alle Kontakte, auch Absagen und Rückrufe – Grundlage fürs
// Nachfassen) und je Regelgebiet dessen Bestand (z. B. Premium ab 3 Mio. €, B-26).
function BrokerLeadsHub() {
  const contacts = useBrokerLeads();
  const tabs: HubTab[] = [
    {
      key: 'liste',
      label: 'Mit Termin',
      render: () => (
        <LeadsPage
          embedded
          mode="own"
          fixed={withMyAppointments}
          emptyText="Noch keine Leads mit Terminen. Sobald ein Termin für Sie eingetragen ist, erscheint der Lead hier."
        />
      ),
    },
    { key: 'kontakte', label: 'Kontakte & Nachfassen', render: () => <BrokerContacts /> },
    { key: 'kaufgesuche', label: 'Kaufgesuche', render: () => <BuyerRequestsPage embedded /> },
    ...(contacts.data?.rule_areas ?? []).map((area) => ({
      key: `regel-${area.id}`,
      label: area.name,
      render: () => (
        <LeadsPage
          embedded
          mode="pool"
          fixed={{ assigned_field_sales_user_id: 'me', area_id: area.id }}
          emptyText="In diesem Regelgebiet liegen derzeit keine Leads bei Ihnen."
        />
      ),
    })),
  ];
  return (
    <Hub
      title="Meine Leads"
      description={
        contacts.data?.rule_areas.length
          ? 'Ihre Leads mit Terminen, alle Kontakte zum Nachfassen, Ihre Kaufgesuche und der Bestand Ihres Regelgebiets.'
          : 'Ihre Leads mit Terminen, alle Kontakte zum Nachfassen und Ihre Kaufgesuche.'
      }
      tabs={tabs}
      label="Ansicht"
    />
  );
}

// Leads: ein Bestand, drei Blickwinkel – Liste (Suche/Filter, u. a. „Adresse unklar“), Karte und Regionen.
export function LeadsHub() {
  const { can } = useProject();
  const fieldOnly = useFieldOnly();
  if (fieldOnly) return <BrokerLeadsHub />;
  const tabs: HubTab[] = [{ key: 'liste', label: 'Liste', render: () => <LeadsPage embedded /> }];
  if (can('area.read'))
    tabs.push({
      key: 'karte',
      label: 'Karte',
      render: () => (
        <Suspense fallback={<PageLoader />}>
          <MapPage embedded />
        </Suspense>
      ),
    });
  tabs.push({ key: 'regionen', label: 'Regionen', render: () => <RegionsPage embedded /> });
  return (
    <Hub
      title="Leads"
      description="Alle Leads des Projekts – suchen und filtern, auf der Karte und nach Regionen."
      tabs={tabs}
      label="Ansicht"
      actions={<LeadCreateButton />}
    />
  );
}
